import React, { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { OverlayView, Polygon, Polyline } from '@react-google-maps/api';
import { useShouldHighlight } from '@hooks/map-hooks';
import { mapConfig } from '@constants/component-configs';
import {
  getMapBoundariesVisible,
  getMapLabelsVisible
} from '@selectors/groups-selector';
import { getHoverGroupId, getTrayGroupIds } from '@selectors/map-selector';
import { generatePath, generatePolygonPaths } from '@utils/map-utils';
import './group-figure.scss';

const icons = [{
  icon: {
    path: 'M 0,-2 0,2',
    strokeOpacity: 1,
    strokeColor: '#FF0000',
    scale: 2
  },
  offset: '0',
  repeat: '20px'
}];

const highlightIcons = [{
  icon: {
    path: 'M 0,-1 0,1',
    strokeOpacity: 1,
    strokeColor: '#FF0000',
    scale: 4
  },
  offset: '0',
  repeat: '20px'
}];

const groupOptions = {
  fillColor: '#FF0000', strokeColor: '#FF0000', strokeOpacity: 0.0, icons, fillOpacity: 0.1
};

const groupHighlightOptions = {
  ...groupOptions, icons: highlightIcons, fillOpacity: 0.3, zIndex: 200
};

const MapGroup = ({ autoArea, center, id, name }) => {
  const boundariesVisible = useSelector(getMapBoundariesVisible);
  const labelsVisible = useSelector(getMapLabelsVisible);

  // Use the custom hook for highlight logic
  const { shouldHighlight } = useShouldHighlight(id, getTrayGroupIds, getHoverGroupId);

  const { shape } = autoArea;

  const options = useMemo(
    () => ({ ...mapConfig.defaultStyle, ...(shouldHighlight ? groupHighlightOptions : groupOptions) }),
    [shouldHighlight]
  );

  const { rings, polygons } = useMemo(() => {
    if (shape.type === 'Polygon') {
      return {
        rings: shape.coordinates.map(generatePath),
        polygons: [
          <Polygon key="polygon-0" paths={generatePolygonPaths(shape.coordinates)} options={options} />
        ]
      };
    } else if (shape.type === 'MultiPolygon') {
      // For MultiPolygon, collect all rings from all polygons and create separate polygons
      return {
        rings: shape.coordinates.flatMap(polygonCoords =>
          polygonCoords.map(ringCoords => generatePath(ringCoords))
        ),
        polygons: shape.coordinates.map((polygonCoords, index) => (
          <Polygon
            key={`polygon-${index}`}
            paths={generatePolygonPaths(polygonCoords)}
            options={options}
          />
        ))
      };
    }
    return { rings: [], polygons: [] };
  }, [shape, options]);

  const labelClass = `group-label ${shouldHighlight ? 'active' : ''}`;

  return (
    <div>
      {boundariesVisible && (
        <div>
          <div>
            {rings.map((path, index) => <Polyline key={`polyline-${index}`} path={path} options={options} />)}
          </div>
          {polygons}
        </div>
      )}
      {labelsVisible && (
        <OverlayView
          mapPaneName={OverlayView.FLOAT_PANE}
          position={{ lng: center[0], lat: center[1] }}
          zIndex={200}
        >
          <div styleName={labelClass}>
            {name}
          </div>
        </OverlayView>
      )}
    </div>
  );
};

MapGroup.propTypes = {
  autoArea: PropTypes.shape({
    shape: PropTypes.shape({
      coordinates: PropTypes.array,
      type: PropTypes.string
    })
  }),
  center: PropTypes.array,
  id: PropTypes.number,
  name: PropTypes.string
};

export default memo(MapGroup);
